import { Star } from "lucide-react";
import { ScrollReveal } from "./ScrollReveal";
import { useRef } from "react";
import avatarJas from "@/assets/avatar-jas.png";
import avatarJames from "@/assets/avatar-james.png";
import avatarKemi from "@/assets/avatar-kemi.png";
import avatarTara from "@/assets/avatar-tara.png";
import avatarMindset from "@/assets/avatar-mindset.png";
import avatarGabriel from "@/assets/avatar-gabriel.png";

const testimonials = [
  { handle: "@fitnesswith_jas", followers: "1,200", niche: "Fitness", result: "$1,247 in 14 days", quote: "I had no idea what to sell. The AI figured it out for me and built the entire product.", stars: 5, avatar: avatarJas, fallbackLetter: "F", fallbackColor: "#E74C3C" },
  { handle: "@jamesbuilds", followers: "3,400", niche: "Business", result: "$3,800 in 30 days", quote: "Built a Notion template pack in under 10 minutes. Never thought it'd sell this well.", stars: 5, avatar: avatarJames, fallbackLetter: "J", fallbackColor: "#F39C12" },
  { handle: "@cookingwithkemi", followers: "890", niche: "Food", result: "$640 in 3 weeks", quote: "I've been posting for 2 years and never made a penny. This changed everything.", stars: 5, avatar: avatarKemi, fallbackLetter: "K", fallbackColor: "#9B59B6" },
  { handle: "@techwithtara", followers: "6,100", niche: "Tech", result: "$2,100 from one course", quote: "The course outline was better than anything I could have written myself.", stars: 5, avatar: avatarTara, fallbackLetter: "T", fallbackColor: "#1ABC9C" },
  { handle: "@dailymindset", followers: "520", niche: "Lifestyle", result: "$480 in week one", quote: "I only had 520 followers. I thought I was too small. I was wrong.", stars: 5, avatar: avatarMindset, fallbackLetter: "D", fallbackColor: "#3498DB" },
  { handle: "@growwithgabriel", followers: "14,000", niche: "Marketing", result: "$4,200 from one video", quote: "I wish I'd found this 2 years ago. One product, one video, life changed.", stars: 5, avatar: avatarGabriel, fallbackLetter: "G", fallbackColor: "#2ECC71" },
];

const AvatarWithFallback = ({ src, letter, color, alt }: { src: string; letter: string; color: string; alt: string }) => (
  <div className="w-10 h-10 rounded-full flex-shrink-0 overflow-hidden relative">
    <div className="absolute inset-0 flex items-center justify-center text-white font-bold text-sm" style={{ background: color }}>{letter}</div>
    <img src={src} alt={alt} className="w-full h-full rounded-full object-cover relative z-10" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
  </div>
);

export const TestimonialsSection = () => {
  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <section id="testimonials" className="py-10 md:py-20 relative" style={{ background: "#FAFAFA" }}>
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <ScrollReveal>
          <div className="text-center mb-8 md:mb-16">
            <h2 className="font-display font-bold mb-3 md:mb-4" style={{ color: "#111827", fontSize: "clamp(24px, 3.5vw, 40px)", lineHeight: 1.2 }}>
              Real Creators. Real <span style={{ color: "#F59E0B" }}>Results.</span>
            </h2>
            <p className="text-sm md:text-lg max-w-2xl mx-auto" style={{ color: "#4B5563" }}>
              From 520 followers to 14,000 — every audience size is making money.
            </p>
          </div>
        </ScrollReveal>

        <div
          ref={scrollRef}
          className="flex md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 max-w-6xl mx-auto overflow-x-auto md:overflow-visible scrollbar-hide snap-x snap-mandatory -mx-4 px-4 md:mx-0 md:px-0"
        >
          {testimonials.map((t, i) => (
            <ScrollReveal key={i} delay={i * 100}>
              <div
                className="rounded-2xl p-5 md:p-6 flex flex-col min-w-[280px] md:min-w-0 snap-center bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                style={{ border: "1px solid #E5E7EB" }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <AvatarWithFallback src={t.avatar} letter={t.fallbackLetter} color={t.fallbackColor} alt={t.handle} />
                    <span className="text-sm font-medium" style={{ color: "#111827" }}>{t.handle}</span>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "#F3F4F6", color: "#6B7280" }}>{t.niche}</span>
                </div>
                <div className="mb-3">
                  <span className="text-2xl md:text-3xl font-display font-bold" style={{ color: "#F59E0B" }}>{t.followers}</span>
                  <span className="text-sm ml-2" style={{ color: "#9CA3AF" }}>followers</span>
                </div>
                <p className="font-semibold text-sm md:text-base mb-3 flex-1" style={{ color: "#111827" }}>{t.result}</p>
                <div className="flex gap-0.5 mb-3">
                  {[...Array(t.stars)].map((_, j) => (
                    <Star key={j} className="w-3.5 h-3.5" style={{ color: "#F59E0B", fill: "#F59E0B" }} />
                  ))}
                </div>
                <p className="text-sm" style={{ color: "#6B7280" }}>"{t.quote}"</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};
